import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import {
  X, FloppyDisk, Trash, Sparkle, MagicWand, Image as ImageIcon, ArrowUp, ArrowDown,
  Plus, Link as LinkIcon, EyeSlash, Eye, Warning, FloppyDiskBack, CheckCircle,
  XCircle, ArrowsClockwise, Camera, Crop,
} from "@phosphor-icons/react";
import ImageCropperModal from "@/components/ImageCropperModal";

// Fallback category list (keeps UI working even if /categories not loaded)
const FALLBACK_CATEGORIES = [
  "weather","climate","plants","trees","agriculture","crops","fruits","vegetables",
  "wildlife","birds","insects","oceans","freshwater","floods","drought","wildfires",
  "geological","air-quality","soil","biodiversity","invasive","food-security",
  "fisheries","livestock","pollution","conservation","science","indigenous","policy",
  "energy","health","records","unusual","good-news",
];

const STATUS_ACTIONS = [
  { key: "publish",       label: "Publish",      icon: CheckCircle,       cls: "border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600" },
  { key: "needs_review",  label: "Send to review", icon: ArrowsClockwise, cls: "border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100" },
  { key: "reject",        label: "Reject",       icon: XCircle,           cls: "border-rose-400 bg-rose-50 text-rose-800 hover:bg-rose-100" },
  { key: "archive",       label: "Archive",      icon: FloppyDiskBack,    cls: "border-stone-400 bg-stone-50 text-stone-800 hover:bg-stone-100" },
];

function TabButton({ active, children, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 rounded-full border font-mono-tech text-[10px] tracking-[0.24em] uppercase ${
        active
          ? "bg-[#2A1F14] text-[#E4A15C] border-[#2A1F14]"
          : "border-black/15 bg-white text-[#151312] hover:bg-black/5"
      }`}
    >
      {children}
    </button>
  );
}

async function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result.split(",")[1] || r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function EditArticleModal({ articleId, onClose, onSaved }) {
  const [tab, setTab] = useState("content");
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [regenNotes, setRegenNotes] = useState("");
  const [regenManual, setRegenManual] = useState("");
  const [newMediaUrl, setNewMediaUrl] = useState("");
  const [newMediaCaption, setNewMediaCaption] = useState("");
  const [newMediaCredit, setNewMediaCredit] = useState("");
  const [attachUrl, setAttachUrl] = useState("");
  const [attachNote, setAttachNote] = useState("");
  const [preview, setPreview] = useState(false);

  // Cropping state
  const [cropSrc, setCropSrc] = useState(null);  // File OR blob-URL waiting to be cropped
  const [cropMode, setCropMode] = useState(null); // 'new-upload' | 'existing-{index}'

  // Edit fields
  const [form, setForm] = useState({
    title: "", short_title: "", body: "", one_sentence_summary: "",
    short_description: "", naturepulse_perspective: "", category: "science",
    tags: "", species: "", hide_source_attribution: false,
  });

  useEffect(() => {
    if (!articleId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await http.get(`/nature/stories/${articleId}`);
        const a = data.news || data.story;
        setArticle(a);
        setForm({
          title: a.title || "",
          short_title: a.short_title || "",
          body: a.body || "",
          one_sentence_summary: a.one_sentence_summary || a.summary || "",
          short_description: a.short_description || "",
          naturepulse_perspective: a.naturepulse_perspective || "",
          category: a.category || "science",
          tags: (a.tags || []).join(", "),
          species: a.species || "",
          hide_source_attribution: !!a.hide_source_attribution,
        });
      } catch (e) {
        toast.error("Failed to load article");
      } finally {
        setLoading(false);
      }
    })();
  }, [articleId]);

  const refresh = async () => {
    const { data } = await http.get(`/nature/stories/${articleId}`);
    setArticle(data.news || data.story);
  };

  const saveContent = async (thenAction) => {
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        short_title: form.short_title || null,
        body: form.body,
        one_sentence_summary: form.one_sentence_summary || null,
        short_description: form.short_description || null,
        naturepulse_perspective: form.naturepulse_perspective || null,
        category: form.category,
        tags: form.tags.split(/[,\n]/).map((t) => t.trim()).filter(Boolean),
        species: form.species || null,
        hide_source_attribution: form.hide_source_attribution,
      };
      await http.patch(`/nature/sources/${articleId}`, payload);
      if (thenAction) {
        await http.post(`/nature/sources/${articleId}/publish`, { action: thenAction });
        toast.success(`Saved and marked ${thenAction === "publish" ? "published" : thenAction.replace("_", " ")}`);
      } else {
        toast.success("Saved");
      }
      await refresh();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const publishOnly = async (action) => {
    setBusy(true);
    try {
      await http.post(`/nature/sources/${articleId}/publish`, { action });
      toast.success(`Marked ${action === "publish" ? "published" : action.replace("_", " ")}`);
      await refresh();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const uploadPhotoFile = async (file) => {
    // Open the cropper first — the actual upload happens in `uploadBlob` after crop.
    setCropSrc(file);
    setCropMode("new-upload");
  };

  const uploadBlob = async (blob) => {
    setBusy(true);
    try {
      const b64 = await fileToBase64(new File([blob], "photo.jpg", { type: "image/jpeg" }));
      const { data } = await http.post("/uploads/photo", {
        image_base64: b64,
        content_type: "image/jpeg",
      });
      await http.post(`/nature/sources/${articleId}/media`, {
        url: data.url, media_type: "image",
        caption: newMediaCaption || null, credit: newMediaCredit || null,
      });
      setNewMediaCaption(""); setNewMediaCredit("");
      toast.success("Photo added");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const recropExisting = (index) => {
    const m = article?.media?.[index];
    if (!m?.url) return;
    setCropSrc(m.url);
    setCropMode(`existing-${index}`);
  };

  const uploadCroppedReplacement = async (blob, index) => {
    setBusy(true);
    try {
      const b64 = await fileToBase64(new File([blob], "photo.jpg", { type: "image/jpeg" }));
      const { data } = await http.post("/uploads/photo", {
        image_base64: b64, content_type: "image/jpeg",
      });
      const media = [...(article.media || [])];
      media[index] = { ...media[index], url: data.url };
      await http.patch(`/nature/sources/${articleId}`, { }); // no-op to touch updated_at
      // Directly rewrite the media array via reorder (bypasses individual endpoints)
      // We use a raw update: delete + re-add would lose ordering, so we just update in-place
      // via a tiny bespoke path — reuse update_media by calling caption endpoint w/ new url.
      // Simplest: DELETE old, POST new at same index by rebuilding via reorder helper.
      await http.delete(`/nature/sources/${articleId}/media/${index}`);
      await http.post(`/nature/sources/${articleId}/media`, {
        url: data.url, media_type: "image",
        caption: media[index].caption || null, credit: media[index].credit || null,
      });
      // Now the new photo is at the last index — move it back to `index`.
      const cur = (await http.get(`/nature/stories/${articleId}`)).data.news;
      const totalNow = (cur.media || []).length;
      if (totalNow > 1) {
        const order = Array.from({ length: totalNow }, (_, k) => k);
        // Move last item to `index`
        const [last] = order.splice(totalNow - 1, 1);
        order.splice(index, 0, last);
        await http.post(`/nature/sources/${articleId}/media/reorder`, { order });
      }
      toast.success("Photo re-cropped");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-crop failed");
    } finally {
      setBusy(false);
    }
  };

  const addMediaByUrl = async () => {
    if (!newMediaUrl.trim()) { toast.error("Paste a URL"); return; }
    setBusy(true);
    try {
      await http.post(`/nature/sources/${articleId}/media`, {
        url: newMediaUrl.trim(), media_type: "image",
        caption: newMediaCaption || null, credit: newMediaCredit || null,
      });
      setNewMediaUrl(""); setNewMediaCaption(""); setNewMediaCredit("");
      toast.success("Photo added");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add photo");
    } finally {
      setBusy(false);
    }
  };

  const illustrateMore = async () => {
    setBusy(true);
    try {
      await http.post(`/nature/sources/${articleId}/illustrate`, {}, { timeout: 90000 });
      toast.success("New AI illustration attached");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Illustrate failed");
    } finally {
      setBusy(false);
    }
  };

  const editMedia = async (i, patch) => {
    try {
      await http.patch(`/nature/sources/${articleId}/media/${i}`, patch);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const deleteMedia = async (i) => {
    if (!window.confirm("Remove this photo from the article?")) return;
    try {
      await http.delete(`/nature/sources/${articleId}/media/${i}`);
      toast.success("Photo removed");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const moveMedia = async (i, dir) => {
    const media = article?.media || [];
    const j = i + dir;
    if (j < 0 || j >= media.length) return;
    const order = media.map((_, k) => k);
    [order[i], order[j]] = [order[j], order[i]];
    try {
      await http.post(`/nature/sources/${articleId}/media/reorder`, { order });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reorder failed");
    }
  };

  const attachSource = async () => {
    if (!attachUrl.trim()) return;
    setBusy(true);
    try {
      await http.post(`/nature/sources/${articleId}/attach-source`, {
        url: attachUrl.trim(), notes: attachNote || null,
      });
      setAttachUrl(""); setAttachNote("");
      toast.success("Source attached");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Attach failed");
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    if (!window.confirm("Rewrite this article via AI? Your media stays, but title/body/tags/references will be regenerated. Uses your rate-limit budget.")) return;
    setBusy(true);
    try {
      await http.post(`/nature/sources/${articleId}/regenerate`,
        { notes: regenNotes || null, manual_content: regenManual || null },
        { timeout: 180000 });
      toast.success("Article regenerated");
      setRegenNotes(""); setRegenManual("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regenerate failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteArticle = async () => {
    if (!window.confirm("PERMANENTLY delete this article? This cannot be undone.")) return;
    setBusy(true);
    try {
      await http.delete(`/nature/sources/${articleId}`);
      toast.success("Article deleted");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const currentStatus = article?.article_status || article?.moderation_status || "needs_review";

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm overflow-y-auto"
      data-testid="edit-article-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="min-h-screen py-6 px-4 flex items-start justify-center">
        <div className="bg-[#F5EBDC] rounded-2xl border border-black/15 w-full max-w-4xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-[#2A1F14] text-[#E4A15C] px-5 py-3 flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="font-mono-tech text-[9px] tracking-[0.32em] uppercase opacity-80">
                Source-to-Story Editor · {currentStatus}
              </div>
              <div className="font-serif-display text-lg truncate">
                {loading ? "Loading…" : (form.title || "Untitled")}
              </div>
            </div>
            <a
              href={`/stories/${articleId}`}
              target="_blank"
              rel="noreferrer"
              className="hidden md:inline-flex items-center gap-1 rounded-full border border-[#E4A15C]/25 px-3 py-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase hover:bg-[#E4A15C]/10"
            >
              <Eye size={11} weight="fill" /> Open live
            </a>
            <button
              onClick={onClose}
              data-testid="edit-close"
              className="rounded-full p-1.5 hover:bg-[#E4A15C]/10"
            >
              <X size={16} weight="bold" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 px-5 pt-4">
            <TabButton active={tab === "content"} onClick={() => setTab("content")} testid="edit-tab-content">Content</TabButton>
            <TabButton active={tab === "media"} onClick={() => setTab("media")} testid="edit-tab-media">
              Photos ({article?.media?.length || 0})
            </TabButton>
            <TabButton active={tab === "sources"} onClick={() => setTab("sources")} testid="edit-tab-sources">
              Sources ({article?.sources?.length || 0})
            </TabButton>
            <TabButton active={tab === "settings"} onClick={() => setTab("settings")} testid="edit-tab-settings">Settings</TabButton>
            <TabButton active={tab === "regen"} onClick={() => setTab("regen")} testid="edit-tab-regen">Regenerate</TabButton>
          </div>

          {/* Body */}
          <div className="p-5 space-y-4">
            {loading ? (
              <div className="text-center py-12 text-[#3A3830]/70">Loading article…</div>
            ) : tab === "content" ? (
              <div className="space-y-3">
                <div>
                  <label className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]/80 block mb-1">Title</label>
                  <input
                    data-testid="edit-title"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-lg font-serif-display outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-2">
                    <label className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]/80 block mb-1">Short title (for cards)</label>
                    <input
                      data-testid="edit-short-title"
                      value={form.short_title}
                      onChange={(e) => setForm({ ...form, short_title: e.target.value })}
                      className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
                    />
                  </div>
                  <div>
                    <label className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]/80 block mb-1">Category</label>
                    <select
                      data-testid="edit-category"
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
                    >
                      {FALLBACK_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]/80 block mb-1">
                    Summary (one sentence)
                  </label>
                  <textarea
                    data-testid="edit-summary"
                    value={form.one_sentence_summary}
                    onChange={(e) => setForm({ ...form, one_sentence_summary: e.target.value })}
                    rows={2}
                    className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--np-brand,#B85F2A)] resize-y"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]/80">
                      Body (markdown)
                    </label>
                    <button
                      onClick={() => setPreview(!preview)}
                      data-testid="edit-preview-toggle"
                      className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:underline"
                    >
                      {preview ? "Edit" : "Preview"}
                    </button>
                  </div>
                  {preview ? (
                    <div className="rounded-lg border border-black/15 bg-white p-4 min-h-[300px] max-h-[500px] overflow-y-auto whitespace-pre-wrap text-[15px] leading-[1.65] font-serif-display text-[#241209]">
                      {form.body}
                    </div>
                  ) : (
                    <textarea
                      data-testid="edit-body"
                      value={form.body}
                      onChange={(e) => setForm({ ...form, body: e.target.value })}
                      rows={18}
                      className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm font-mono outline-none focus:border-[color:var(--np-brand,#B85F2A)] resize-y leading-relaxed"
                    />
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]/80 block mb-1">Tags (comma-separated)</label>
                    <input
                      data-testid="edit-tags"
                      value={form.tags}
                      onChange={(e) => setForm({ ...form, tags: e.target.value })}
                      className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
                    />
                  </div>
                  <div>
                    <label className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]/80 block mb-1">Species (common name)</label>
                    <input
                      data-testid="edit-species"
                      value={form.species}
                      onChange={(e) => setForm({ ...form, species: e.target.value })}
                      className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
                    />
                  </div>
                </div>
                <div>
                  <label className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]/80 block mb-1">
                    NaturePulse perspective (closing line)
                  </label>
                  <textarea
                    data-testid="edit-np-perspective"
                    value={form.naturepulse_perspective}
                    onChange={(e) => setForm({ ...form, naturepulse_perspective: e.target.value })}
                    rows={2}
                    className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--np-brand,#B85F2A)] resize-y"
                  />
                </div>
              </div>
            ) : tab === "media" ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {(article?.media || []).map((m, i) => (
                    <div key={i} data-testid={`edit-media-${i}`} className="rounded-lg border border-black/15 bg-white p-2 flex flex-col gap-2">
                      {m.url ? (
                        <img src={m.url} alt={m.caption || ""} className="w-full aspect-square object-cover rounded" />
                      ) : (
                        <div className="w-full aspect-square rounded bg-black/5 grid place-items-center">
                          <ImageIcon size={20} className="opacity-50" />
                        </div>
                      )}
                      <input
                        data-testid={`edit-media-caption-${i}`}
                        defaultValue={m.caption || ""}
                        onBlur={(e) => e.target.value !== (m.caption || "") && editMedia(i, { caption: e.target.value })}
                        placeholder="Caption"
                        className="w-full rounded border border-black/15 bg-white px-2 py-1 text-[11px] outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
                      />
                      <input
                        data-testid={`edit-media-credit-${i}`}
                        defaultValue={m.credit || ""}
                        onBlur={(e) => e.target.value !== (m.credit || "") && editMedia(i, { credit: e.target.value })}
                        placeholder="Credit"
                        className="w-full rounded border border-black/15 bg-white px-2 py-1 text-[11px] outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
                      />
                      <div className="flex items-center justify-between">
                        <div className="flex gap-1">
                          <button
                            disabled={i === 0}
                            onClick={() => moveMedia(i, -1)}
                            data-testid={`edit-media-up-${i}`}
                            className="rounded p-1 border border-black/15 bg-white hover:bg-black/5 disabled:opacity-30"
                          ><ArrowUp size={11} weight="bold" /></button>
                          <button
                            disabled={i === (article.media.length - 1)}
                            onClick={() => moveMedia(i, +1)}
                            data-testid={`edit-media-down-${i}`}
                            className="rounded p-1 border border-black/15 bg-white hover:bg-black/5 disabled:opacity-30"
                          ><ArrowDown size={11} weight="bold" /></button>
                          <button
                            onClick={() => recropExisting(i)}
                            data-testid={`edit-media-crop-${i}`}
                            className="rounded p-1 border border-black/15 bg-white hover:bg-black/5"
                            title="Re-crop / rotate this photo"
                          ><Crop size={11} weight="bold" /></button>
                        </div>
                        <button
                          onClick={() => deleteMedia(i)}
                          data-testid={`edit-media-delete-${i}`}
                          className="rounded p-1 border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100"
                        ><Trash size={11} weight="bold" /></button>
                      </div>
                      {i === 0 && (
                        <div className="font-mono-tech text-[8px] tracking-[0.22em] uppercase text-[color:var(--np-brand,#B85F2A)] text-center">
                          Cover
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {(article?.media || []).length === 0 && (
                  <div className="rounded-lg border border-dashed border-black/15 p-6 text-center text-[#3A3830]/70 text-sm">
                    No photos attached yet.
                  </div>
                )}

                <div className="border-t border-black/10 pt-4 space-y-3">
                  <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#151312]">Add photo</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <input
                      data-testid="edit-media-new-caption"
                      value={newMediaCaption}
                      onChange={(e) => setNewMediaCaption(e.target.value)}
                      placeholder="Caption (optional)"
                      className="rounded border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
                    />
                    <input
                      data-testid="edit-media-new-credit"
                      value={newMediaCredit}
                      onChange={(e) => setNewMediaCredit(e.target.value)}
                      placeholder="Credit (optional)"
                      className="rounded border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <label className="inline-flex items-center justify-center gap-1.5 rounded-full border border-black/25 bg-white px-3 py-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase cursor-pointer hover:bg-black/5">
                      <Camera size={13} weight="fill" /> Upload from device
                      <input
                        type="file"
                        accept="image/*"
                        data-testid="edit-media-file"
                        onChange={(e) => e.target.files?.[0] && uploadPhotoFile(e.target.files[0])}
                        className="hidden"
                      />
                    </label>
                    <div className="flex gap-1 col-span-2">
                      <input
                        data-testid="edit-media-new-url"
                        value={newMediaUrl}
                        onChange={(e) => setNewMediaUrl(e.target.value)}
                        placeholder="…or paste an image URL"
                        className="flex-1 rounded-full border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
                      />
                      <button
                        onClick={addMediaByUrl}
                        disabled={busy || !newMediaUrl.trim()}
                        data-testid="edit-media-add-url"
                        className="rounded-full px-3 py-2 border border-[color:var(--np-brand,#B85F2A)] bg-white text-[color:var(--np-brand,#B85F2A)] font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:bg-[color:var(--np-brand,#B85F2A)] hover:text-white disabled:opacity-40"
                      ><Plus size={12} weight="bold" /></button>
                    </div>
                  </div>
                  <button
                    onClick={illustrateMore}
                    disabled={busy}
                    data-testid="edit-media-illustrate"
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 border border-[#151312] bg-[#151312] text-[#E4A15C] font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:bg-[#E4A15C] hover:text-[#151312] disabled:opacity-40"
                  >
                    <Sparkle size={12} weight="fill" /> {busy ? "Working…" : "AI illustrate — add one more image"}
                  </button>
                  <div className="text-[10px] text-[#3A3830]/60 font-mono-tech tracking-widest uppercase mt-1">
                    Uploads: JPG/PNG/WebP · max 20 MB · you'll be prompted to crop / rotate before publishing
                  </div>
                </div>
              </div>
            ) : tab === "sources" ? (
              <div className="space-y-3">
                <div className="space-y-2">
                  {(article?.sources || []).map((s, i) => (
                    <div key={i} className="rounded-lg border border-black/15 bg-white p-3 flex gap-3">
                      {s.thumbnail && (
                        <img src={s.thumbnail} alt="" className="w-16 h-16 object-cover rounded" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                          {s.platform} · {s.source_type}
                        </div>
                        <div className="font-serif-display text-sm mt-0.5 line-clamp-1">{s.title}</div>
                        <div className="text-xs text-[#3A3830] mt-0.5">{s.creator || s.publisher}</div>
                        <a href={s.canonical_url || s.url} target="_blank" rel="noreferrer"
                          className="text-[11px] text-[color:var(--np-brand,#B85F2A)] hover:underline break-all">
                          {s.canonical_url || s.url}
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-black/10 pt-3 space-y-2">
                  <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#151312]">Attach another source URL</div>
                  <input
                    data-testid="edit-attach-url"
                    value={attachUrl}
                    onChange={(e) => setAttachUrl(e.target.value)}
                    placeholder="https://…"
                    className="w-full rounded-full border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
                  />
                  <input
                    data-testid="edit-attach-note"
                    value={attachNote}
                    onChange={(e) => setAttachNote(e.target.value)}
                    placeholder="Note (optional)"
                    className="w-full rounded-full border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
                  />
                  <button
                    onClick={attachSource}
                    disabled={busy || !attachUrl.trim()}
                    data-testid="edit-attach-btn"
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 border border-[color:var(--np-brand,#B85F2A)] bg-white text-[color:var(--np-brand,#B85F2A)] font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:bg-[color:var(--np-brand,#B85F2A)] hover:text-white disabled:opacity-40"
                  >
                    <LinkIcon size={12} weight="fill" /> Attach source
                  </button>
                </div>
              </div>
            ) : tab === "settings" ? (
              <div className="space-y-4">
                <label className="flex items-start gap-3 rounded-lg border border-black/15 bg-white p-3 cursor-pointer hover:bg-[#FFF6E5]">
                  <input
                    type="checkbox"
                    data-testid="edit-hide-attribution"
                    checked={form.hide_source_attribution}
                    onChange={(e) => setForm({ ...form, hide_source_attribution: e.target.checked })}
                    className="mt-0.5 h-4 w-4"
                  />
                  <div className="flex-1">
                    <div className="font-serif-display text-base flex items-center gap-2">
                      {form.hide_source_attribution ? <EyeSlash size={14} /> : <Eye size={14} />}
                      Hide source attribution on the article page
                    </div>
                    <div className="text-xs text-[#3A3830]/80 mt-0.5">
                      When on: readers won't see the "inspired by…" opening line, the original-source card, or the claims-verification grid.
                      A subtle "Editorial article" badge appears instead. Source data stays in the admin panel.
                    </div>
                  </div>
                </label>

                <div className="rounded-lg border border-rose-300 bg-rose-50 p-3">
                  <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-rose-800 mb-1">Danger zone</div>
                  <div className="text-sm text-rose-900 mb-2">
                    Permanently delete this article. This cannot be undone.
                  </div>
                  <button
                    onClick={deleteArticle}
                    disabled={busy}
                    data-testid="edit-delete-article"
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 border border-rose-500 bg-rose-500 text-white font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:bg-rose-600 disabled:opacity-40"
                  >
                    <Trash size={12} weight="fill" /> Delete permanently
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2">
                  <Warning size={14} weight="fill" className="mt-0.5 flex-shrink-0" />
                  <div>
                    Regenerating rewrites the article text, tags and references via AI while
                    preserving your photos and this article's ID. Use for a first-pass rewrite
                    when you've realized the AI missed the subject entirely — or you can just
                    fine-tune the fields in the Content tab.
                  </div>
                </div>
                <div>
                  <label className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]/80 block mb-1">
                    Rewrite guidance / angle
                  </label>
                  <input
                    data-testid="edit-regen-notes"
                    value={regenNotes}
                    onChange={(e) => setRegenNotes(e.target.value)}
                    placeholder="e.g. 'Focus on species conservation angle, keep tone factual for kids'"
                    className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
                  />
                </div>
                <div>
                  <label className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]/80 block mb-1">
                    Describe what the source actually shows (helps for blocked platforms)
                  </label>
                  <textarea
                    data-testid="edit-regen-manual"
                    value={regenManual}
                    onChange={(e) => setRegenManual(e.target.value)}
                    rows={5}
                    placeholder="Optional: paste a description of the video/post…"
                    className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--np-brand,#B85F2A)] resize-y"
                  />
                </div>
                <button
                  onClick={regenerate}
                  disabled={busy}
                  data-testid="edit-regen-btn"
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 border border-[#151312] bg-[#151312] text-[#E4A15C] font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-[#E4A15C] hover:text-[#151312] disabled:opacity-40"
                >
                  <MagicWand size={13} weight="fill" /> {busy ? "Regenerating…" : "Regenerate article"}
                </button>
              </div>
            )}
          </div>

          {/* Footer action bar (only for Content tab) */}
          {!loading && tab === "content" && (
            <div className="sticky bottom-0 z-10 bg-white border-t border-black/15 px-5 py-3 flex flex-wrap gap-2 items-center">
              <button
                onClick={() => saveContent(null)}
                disabled={saving}
                data-testid="edit-save"
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 border border-[color:var(--np-brand,#B85F2A)] bg-white text-[color:var(--np-brand,#B85F2A)] font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-[color:var(--np-brand,#B85F2A)] hover:text-white disabled:opacity-40"
              >
                <FloppyDisk size={12} weight="fill" /> {saving ? "Saving…" : "Save changes"}
              </button>
              {STATUS_ACTIONS.map((a) => (
                <button
                  key={a.key}
                  onClick={() => saveContent(a.key)}
                  disabled={saving || busy}
                  data-testid={`edit-save-and-${a.key}`}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 border font-mono-tech text-[10px] tracking-[0.24em] uppercase disabled:opacity-40 ${a.cls}`}
                >
                  <a.icon size={12} weight="fill" /> Save & {a.label}
                </button>
              ))}
            </div>
          )}
          {!loading && tab !== "content" && (
            <div className="sticky bottom-0 z-10 bg-white border-t border-black/15 px-5 py-3 flex flex-wrap gap-2 items-center">
              <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#3A3830]/70 mr-auto">
                Quick status
              </div>
              {STATUS_ACTIONS.map((a) => (
                <button
                  key={a.key}
                  onClick={() => publishOnly(a.key)}
                  disabled={busy || saving}
                  data-testid={`edit-quick-${a.key}`}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 border font-mono-tech text-[10px] tracking-[0.24em] uppercase disabled:opacity-40 ${a.cls}`}
                >
                  <a.icon size={12} weight="fill" /> {a.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {cropSrc && (
        <ImageCropperModal
          file={typeof cropSrc === "string" ? null : cropSrc}
          src={typeof cropSrc === "string" ? cropSrc : null}
          onCancel={() => { setCropSrc(null); setCropMode(null); }}
          onConfirm={async (blob) => {
            const mode = cropMode;
            setCropSrc(null); setCropMode(null);
            if (mode === "new-upload") {
              await uploadBlob(blob);
            } else if (mode?.startsWith("existing-")) {
              const idx = Number(mode.split("-")[1]);
              await uploadCroppedReplacement(blob, idx);
            }
          }}
        />
      )}
    </div>
  );
}
